import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetBulkPurchasesQuery } from "../../../service/bulk-purchase-service";
import { useLazyGetUtilitiesQuery } from "../../../service/utility-service";
import { BulkPurchaseTransaction } from "../../../model/bulk-purchase/bulk-purchase";
import { useConfigLayout } from "../../../hooks/useConfigLayout";
import BillPageLayout from "../bills/component/layout/BillPageLayout";
import { Utility } from "../../../model/bill-settings/bill-settings";
import UtilityCard from "../../../common/components/xepp/utility-card/UtilityCard";
import utilCardStyles from "../../../common/components/xepp/utility-card/UtilityCard.module.scss";
import EmptyState from "../../../common/components/xepp/empty-state/EmptyState";
import { CardIcon, PaymentIcon, SearchIcon, ThreeDotsIcon, UtilityIcon, WalletIcon } from "../../../assets/icons";
import { SkeletonGrid } from "../../../common/components/xepp/skeleton-loader/SkeletonLoader";
import { TablePagination } from "../../../common/components/xepp/table-pagination/TablePagination";
import billStyles from "../bills/bills.module.scss";
import styles from "./BulkPurchasePage.module.scss";
import { ApplicationRoutes } from "../../../routes";
import { formatDateTime } from "../../admin/bulk-purchase/utils";

type PaymentMethodConfig = {
	label: string;
	Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const PAYMENT_METHODS: Record<number, PaymentMethodConfig> = {
	1: { label: "Payment Hub", Icon: CardIcon },
	2: { label: "Cash Payment", Icon: WalletIcon },
};

const getPaymentMethodConfig = (type: number): PaymentMethodConfig => {
	return PAYMENT_METHODS[type] ?? { label: "Unknown", Icon: PaymentIcon };
};

const getStatusVariant = (status?: number) => {
	switch (status) {
		case 2:
			return "success";
		case 3:
			return "failed";
		case 1:
			return "pending";
		default:
			return "unknown";
	}
};

const getStatusLabel = (status?: number) => {
	switch (status) {
		case 2:
			return "Success";
		case 3:
			return "Failed";
		case 1:
			return "Pending";
		default:
			return "Unknown";
	}
};

const BulkPurchasePage = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedUtility, setSelectedUtility] = useState<Utility | undefined>();
	const pageSize = 10;
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const utilityIdParam = searchParams.get("utilityId");

	const { updateLayout, selectedCountry } = useConfigLayout({
		showCountrySelector: true,
		utility: null,
		showExportButton: false,
		showBreadCrumb: true,
	});

	const clearSelection = useCallback(() => {
		setSelectedUtility(undefined);
		setSearchParams({}, { replace: true });
	}, [setSearchParams]);

	const [
		fetchUtilities,
		{ data: utilities = [], isLoading: isUtilitiesLoading, isFetching: isUtilitiesFetching },
	] = useLazyGetUtilitiesQuery();

useEffect(() => {
	if (selectedCountry?.isoCode) {
		fetchUtilities(selectedCountry.isoCode);
	}
	setSelectedUtility(undefined);
}, [selectedCountry?.isoCode, fetchUtilities]);

	useEffect(() => {
		if (selectedUtility) {
			updateLayout({
				utility: selectedUtility,
				showCountrySelector: false,
				goBackHandler: clearSelection,
				showExportButton: false,
			});
		} else {
			updateLayout({
				utility: null,
				showCountrySelector: true,
				goBackHandler: null,
				showExportButton: false,
			});
			setSearchTerm("");
			setCurrentPage(1);
		}
	}, [selectedUtility, updateLayout, clearSelection]);

	useEffect(() => {
		if (utilities.length === 0) {
			return;
		}

		if (utilityIdParam) {
			const match = utilities.find((utility) => utility.id === utilityIdParam);
			if (match) {
				if (!selectedUtility || selectedUtility.id !== match.id) {
					setSelectedUtility(match);
				}
				return;
			}
			setSearchParams({}, { replace: true });
		}
	}, [utilities, utilityIdParam, selectedUtility, setSearchParams]);

	useEffect(() => {
		if (
			selectedUtility &&
			utilities.length > 0 &&
			!utilities.some((utility) => utility.id === selectedUtility.id)
		) {
			setSelectedUtility(undefined);
			setSearchParams({}, { replace: true });
		}
	}, [utilities, selectedUtility, setSearchParams]);

	useEffect(() => {
		if (selectedUtility) {
			setCurrentPage(1);
		}
	}, [selectedUtility?.id]);

	const shouldSkipBulkPurchases = !selectedUtility?.id || !selectedCountry?.isoCode;

	const {
		data,
		isLoading: isBulkPurchasesLoading,
		isFetching: isBulkPurchasesFetching,
		error: bulkPurchasesError,
	} = useGetBulkPurchasesQuery(
		{
			page: currentPage,
			pageSize,
			search: searchTerm || undefined,
			utilityId: selectedUtility?.id,
			countryCode: selectedCountry?.isoCode,
		},
		{ skip: shouldSkipBulkPurchases }
	);

	const isBulkListLoading = isBulkPurchasesLoading || isBulkPurchasesFetching;
	const isUtilityListLoading = isUtilitiesLoading || isUtilitiesFetching;

	const handleOpenDetails = (reference: string) => {
		if (!reference) {
			return;
		}
		const searchString = searchParams.toString();
		navigate(
			`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.ADMIN_BILLS}/${ApplicationRoutes.BULK_PURCHASE}/details/${encodeURIComponent(
				reference
			)}`,
			{
				state: searchString ? { bulkPurchaseSearch: searchString } : undefined,
			}
		);
	};

	const handleSelectUtility = (utility: Utility) => {
		setSelectedUtility(utility);
		setSearchParams({ utilityId: utility.id });
	};

	const getTotalMeters = (transaction: BulkPurchaseTransaction) => {
		return (
			(transaction.bulkPurchaseDetails?.vendingDetailsList?.length || 0) +
			(transaction.bulkPurchaseDetails?.billingDetailsList?.length || 0)
		);
	};

	const renderTableRows = () => {
		if (isBulkListLoading) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>Loading bulk purchases...</td>
				</tr>
			);
		}

		if (bulkPurchasesError) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>Unable to load bulk purchases.</td>
				</tr>
			);
		}

		const items = data?.items ?? [];

		if (items.length === 0) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>No bulk purchases found.</td>
				</tr>
			);
		}

		return items.map((transaction: BulkPurchaseTransaction) => {
			const { Icon: MethodIcon, label } = getPaymentMethodConfig(transaction.type);
			const statusVariant = getStatusVariant(transaction.status);
			const statusLabel = getStatusLabel(transaction.status);

			return (
				<tr key={transaction.id}>
					<td>
						<div className={styles.purchaserDetails}>
							<div className={styles.purchaserName}>
								{transaction.user.firstName} {transaction.user.lastName}
							</div>
						</div>
					</td>
					<td className={styles.referenceCell}>{transaction.reference}</td>
					<td className={styles.dateCell}>{formatDateTime(transaction.createdAt)}</td>
					<td className={styles.metersCell}>
						<span className={styles.metersLabel}>Total Number of Meters:</span>
						<span className={styles.metersBadge}>{getTotalMeters(transaction)}</span>
					</td>
					<td className={styles.statusCell}>
						<span
							className={`${styles.statusBadge} ${
								styles[`status${statusVariant.charAt(0).toUpperCase() + statusVariant.slice(1)}`] ?? ""
							}`}>
							<span className={styles.statusDot} />
							{statusLabel}
						</span>
					</td>
					<td className={styles.paymentCell}>
						<div className={styles.paymentBadge}>
							<span className={styles.paymentIcon}>
								<MethodIcon />
							</span>
							<span>{label}</span>
						</div>
					</td>
					<td className={styles.actionCell}>
						<button
							type="button"
							className={styles.optionsButton}
							onClick={() => handleOpenDetails(transaction.reference)}
							aria-label="View bulk purchase details"
						>
							<ThreeDotsIcon />
						</button>
					</td>
				</tr>
			);
		});
	};

	return (
		<BillPageLayout title={selectedUtility ? "Bulk Purchases" : ""}>
			{selectedUtility ? (
				<div className={styles.bulkPurchaseWrapper}>
					<div className={styles.searchContainer}>
						<div className={styles.searchInput}>
							<input
								type="text"
								placeholder="Search by Name"
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value.trim())}
							/>
							<span className={styles.searchIcon}>
								<SearchIcon />
							</span>
						</div>
					</div>

					<div className={styles.tableContainer}>
						<table className={styles.transactionsTable}>
							<thead>
								<tr>
									<th scope="col">Purchaser</th>
									<th scope="col">Reference</th>
									<th scope="col">Date &amp; Time</th>
									<th scope="col">Total Meters</th>
									<th scope="col">Status</th>
									<th scope="col">Payment Method</th>
									<th scope="col" className={styles.actionHeader}>
										Action
									</th>
								</tr>
							</thead>
							<tbody>{renderTableRows()}</tbody>
						</table>
					</div>

					<div className={styles.paginationContainer}>
						<TablePagination
							isLoading={isBulkListLoading}
							totalItems={data?.count ?? 0}
							itemsPerPage={pageSize}
							currentPage={currentPage}
							onPageChange={setCurrentPage}
						/>
					</div>
				</div>
			) : (
				<div className={billStyles.container}>
					{isUtilityListLoading && <SkeletonGrid />}
					<div className={utilCardStyles.utilityGrid}>
						{utilities.length === 0 && !isUtilityListLoading ? (
							<EmptyState icon={<UtilityIcon />} title="No utilities available" />
						) : (
							utilities.map((utilityItem) => (
								<div key={utilityItem.utilityCode} className={utilCardStyles.utilityCardWrapper}>
									<UtilityCard
										logo={utilityItem.imageUrl}
										name={utilityItem.name}
										description={utilityItem?.description ?? ""}
										onManage={() => handleSelectUtility(utilityItem)}
									/>
								</div>
							))
						)}
					</div>
				</div>
			)}
		</BillPageLayout>
	);
};

export default BulkPurchasePage;





